import React from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowRight, BookLock, Check, Footprints, HeartHandshake, Lock, ShieldCheck, Sprout, Sun } from "lucide-react";

export function Shapes() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="blob blob-a w-[520px] h-[520px] -top-40 -left-32" />
      <div className="blob blob-b w-[620px] h-[620px] top-24 -right-56" />
      <div className="blob blob-c w-[480px] h-[480px] -bottom-40 left-1/4" />
      <div className="ring-shape w-[360px] h-[360px] top-16 right-[8%] hidden md:block" />
      <div className="ring-shape w-[200px] h-[200px] top-[300px] right-[22%] hidden md:block" />
    </div>
  );
}

export function Logo() {
  return <Link to="/benvenuto" className="font-display text-xl font-extrabold tracking-tight" data-testid="brand-logo">UNSEEN<span className="text-[hsl(var(--primary))]">.</span></Link>;
}

function Preview() {
  return (
    <div className="relative w-full max-w-sm mx-auto lg:mx-0 space-y-3" data-testid="landing-preview" aria-hidden="true">
      <div className="surface p-5 space-y-3">
        <div className="text-xs text-ink/55">Oggi</div>
        <div className="font-semibold text-lg">Come ti senti oggi?</div>
        <div className="composer px-4 py-3 text-sm text-ink/50">Raccontami come ti senti…</div>
        <div className="flex flex-wrap gap-2">{["Sto bene", "Sono in ansia", "Mi sento giù"].map((t) => <span key={t} className="chip">{t}</span>)}</div>
      </div>
      <div className="surface p-4 flex items-center gap-3 ml-8">
        <span className="w-8 h-8 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center shrink-0"><Check size={15} /></span>
        <div className="min-w-0"><div className="font-semibold text-sm">Bere due litri d'acqua</div><div className="text-xs text-ink/60">Obiettivo di oggi · fatto</div></div>
      </div>
      <div className="surface p-4 space-y-2 mr-6">
        <div className="flex items-center justify-between text-sm"><span className="font-semibold">Cuciniamo insieme</span><span className="text-ink/60">Giorno 4 di 7</span></div>
        <div className="h-1.5 rounded-full bg-white/60 overflow-hidden"><div className="h-full w-[57%] bg-[hsl(var(--primary))]" /></div>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: Sun, title: "Oggi", text: "Una domanda semplice ogni giorno: come stai? Scrivi, scegli un umore, fermati un momento." },
  { icon: Sprout, title: "Cresci", text: "Obiettivi piccoli e realistici, giornalieri o settimanali, con un promemoria quando vuoi tu." },
  { icon: Footprints, title: "Facciamolo insieme", text: "Percorsi di più giorni con altre persone: ogni giorno una tappa da condividere. Nessun vincitore." },
  { icon: HeartHandshake, title: "Parla con qualcuno", text: "Chiedi in forma anonima: una persona reale disponibile accetta e si apre una chat privata." },
  { icon: Activity, title: "Corpo e diario", text: "Segna dove senti tensione o fatica e tieni un diario che vedi solo tu." },
];

function Features() {
  return (
    <section className="space-y-8" data-testid="landing-features">
      <div className="max-w-xl space-y-2">
        <div className="label text-[hsl(var(--primary))]">Cosa trovi</div>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Strumenti semplici, per te e con gli altri.</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
        {FEATURES.map((f, i) => (
          <div key={f.title} className={`surface p-6 space-y-3 ${i < 2 ? "lg:col-span-3" : "lg:col-span-2"}`} data-testid="landing-feature">
            <span className="w-10 h-10 rounded-2xl bg-white/70 flex items-center justify-center"><f.icon size={19} className="text-[hsl(var(--primary))]" /></span>
            <h3 className="font-semibold text-lg">{f.title}</h3>
            <p className="text-sm text-ink/70 leading-relaxed">{f.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Privacy() {
  const items = ["Diario, corpo e umore sono privati: nessuno li vede, nemmeno chi gestisce l'app", "Nel feed arriva solo ciò che scegli di pubblicare", "Puoi bloccare e segnalare chiunque, in ogni momento"];
  return (
    <section className="surface p-6 sm:p-10 grid md:grid-cols-[1fr_1.2fr] gap-8 items-center" data-testid="landing-privacy">
      <div className="space-y-3">
        <span className="w-11 h-11 rounded-2xl bg-[hsl(var(--primary))] text-white flex items-center justify-center"><Lock size={19} /></span>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Ciò che scrivi per te <span className="font-serif-i text-[hsl(var(--primary))]">resta tuo.</span></h2>
      </div>
      <ul className="space-y-3">
        {items.map((t) => <li key={t} className="flex gap-3 text-ink/80"><ShieldCheck size={18} className="text-[hsl(var(--primary))] shrink-0 mt-0.5" /><span>{t}</span></li>)}
      </ul>
    </section>
  );
}

function Ctas({ id }: { id: string }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
      <Link to="/registrati" className="btn btn-primary flex-1" data-testid={`${id}-register-btn`}>Crea il tuo spazio<ArrowRight size={16} /></Link>
      <Link to="/accedi" className="btn btn-ghost flex-1" data-testid={`${id}-login-btn`}>Accedi</Link>
    </div>
  );
}

export function Landing() {
  return (
    <div className="relative min-h-[100dvh] overflow-x-hidden" data-testid="landing-page">
      <Shapes />
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-10">
        <header className="flex items-center justify-between py-6">
          <Logo />
          <Link to="/accedi" className="btn btn-ghost min-h-[40px] py-2" data-testid="landing-header-login">Accedi</Link>
        </header>
        <section className="grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center pt-8 pb-20 lg:pt-16 lg:pb-28" data-testid="landing-hero">
          <div className="space-y-7">
            <div className="label text-[hsl(var(--primary))]">Crescita personale, insieme</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.04]">Uno spazio calmo per stare meglio. <span className="font-serif-i text-[hsl(var(--primary))]">Un passo alla volta.</span></h1>
            <p className="text-base text-ink/72 max-w-lg leading-relaxed">UNSEEN ti aiuta ad ascoltarti, a darti piccoli obiettivi e a non sentirti solo: con persone vere, senza classifiche e senza giudizi.</p>
            <Ctas id="landing" />
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink/62" data-testid="landing-trust">
              <span className="inline-flex items-center gap-1.5"><Check size={14} />Gratuito</span>
              <span className="inline-flex items-center gap-1.5"><BookLock size={14} />Diario privato</span>
              <span className="inline-flex items-center gap-1.5"><HeartHandshake size={14} />Persone reali, non un'AI</span>
            </div>
          </div>
          <Preview />
        </section>
        <div className="space-y-20 pb-16">
          <Features />
          <Privacy />
          <section className="text-center space-y-6 py-6" data-testid="landing-final">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Inizia da come stai oggi.</h2>
            <div className="flex justify-center"><Ctas id="landing-final" /></div>
            <p className="text-xs text-ink/55 max-w-lg mx-auto">UNSEEN non sostituisce medici, psicologi o servizi di emergenza. Se sei in pericolo chiama il 112.</p>
          </section>
          <footer className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-white/60 text-sm text-ink/60">
            <Logo />
            <div className="flex gap-5"><Link to="/privacy" data-testid="landing-privacy-link">Privacy</Link><Link to="/termini" data-testid="landing-terms-link">Termini</Link><Link to="/regolamento" data-testid="landing-rules-link">Regolamento</Link></div>
          </footer>
        </div>
      </div>
    </div>
  );
}
